import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Trophy } from "lucide-react";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

/**
 * Cheers Leaderboard — public homepage/directory strip showing the most-cheered
 * businesses. Uses a hand-clap emoji 👏 (NOT the horse) per product request.
 * Drives a viral loop: every business has a public reason to ask customers to
 * cheer them in order to climb the leaderboard.
 */
export default function CheersLeaderboard({ limit = 8 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_V1}/cheers/leaderboard?limit=${limit}`)
      .then((r) => { if (!cancelled) setItems(r.data?.top || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [limit]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white/5 border border-white/10 p-5 animate-pulse" data-testid="cheers-leaderboard-loading">
        <div className="h-5 w-1/3 bg-white/10 rounded mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (<div key={i} className="h-16 bg-white/5 rounded-lg" />))}
        </div>
      </div>
    );
  }
  if (!items.length) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="rounded-2xl bg-gradient-to-r from-[#F59E0B]/10 via-[#0F172A] to-[#EC4899]/10 border border-[#F59E0B]/30 p-5 sm:p-6"
      data-testid="cheers-leaderboard"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl" role="img" aria-label="clapping hands">👏</span>
          <h2 className="text-white font-bold text-lg sm:text-xl font-['Outfit']">Community Cheers Leaderboard</h2>
          <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 text-[#FBBF24] text-xs">
            <Trophy className="w-3 h-3" /> Trending
          </span>
        </div>
        <p className="text-[#94A3B8] text-xs">Top businesses most cheered by their communities</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((biz, idx) => {
          const citySlug = (biz.city || "local").toLowerCase().replace(/\s+/g, "-");
          const stateSlug = (biz.state || "").toLowerCase();
          const cityState = stateSlug ? `${citySlug}-${stateSlug}` : citySlug;
          const url = `/directory/${biz.category_id || "business"}/${cityState}/${biz.slug}`;
          const rankColor = idx === 0 ? "#FBBF24" : idx === 1 ? "#E2E8F0" : idx === 2 ? "#D97706" : "#64748B";
          return (
            <Link
              key={biz.id || biz.slug}
              to={url}
              className="group relative rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#F59E0B]/30 p-3 flex items-center gap-3 transition"
              data-testid={`cheers-leader-${biz.slug}`}
            >
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                style={{ backgroundColor: `${rankColor}22`, color: rankColor }}
              >
                #{idx + 1}
              </span>
              <img
                src={biz.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(biz.name)}&background=7C3AED&color=fff&size=64`}
                alt={biz.name}
                className="w-10 h-10 rounded-md object-cover bg-white/5 flex-shrink-0"
                onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(biz.name)}&background=7C3AED&color=fff&size=64`; }}
              />
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-semibold truncate">{biz.name}</p>
                <p className="text-[#94A3B8] text-xs truncate">
                  <span className="text-[#FBBF24] font-mono">{biz.cheers_count}</span>
                  <span className="ml-1">👏</span>
                  {biz.city ? <span className="ml-2 text-[#64748B]">· {biz.city}{biz.state ? `, ${biz.state}` : ""}</span> : null}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </motion.section>
  );
}
